import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaBell, FaCheckDouble, FaInfoCircle, FaGift, FaCoins, FaNewspaper, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import ApiService from '../../services/ApiService';
import { theme } from '../../styles/theme';

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.gradients.background};
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
  color: white;

  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
    padding-bottom: 80px; /* Space for bottom nav */
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: ${theme.gradients.gold};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  max-width: 800px;
  margin: 0 auto;
`;

const NotificationCard = styled.div`
  background: ${props => {
    if (props.$isSystem) return 'rgba(255, 152, 0, 0.1)'; // Orange tint for system
    if (props.$isGlobal) return 'rgba(33, 150, 243, 0.1)'; // Blue tint for global
    return props.$read ? 'rgba(0, 0, 0, 0.2)' : 'rgba(30, 30, 60, 0.6)';
  }};
  border: 1px solid ${props => {
    if (props.$isSystem) return 'rgba(255, 152, 0, 0.5)';
    if (props.$isGlobal) return 'rgba(33, 150, 243, 0.5)';
    return props.$read ? 'rgba(255, 255, 255, 0.05)' : theme.colors.primary;
  }};
  border-left: 4px solid ${props => getBorderColor(props.type)};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md};
  position: relative;
  transition: all 0.2s;
  cursor: ${props => props.$hasAction ? 'pointer' : 'default'};
  ${props => props.$isSystem && `
    box-shadow: 0 0 20px rgba(255, 152, 0, 0.3);
  `}

  &:hover {
    transform: translateX(4px);
    background: ${props => {
      if (props.$isSystem) return 'rgba(255, 152, 0, 0.15)';
      if (props.$isGlobal) return 'rgba(33, 150, 243, 0.15)';
      return props.$read ? 'rgba(0, 0, 0, 0.3)' : 'rgba(40, 40, 80, 0.7)';
    }};
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  font-size: 1.2rem;
  color: ${props => props.color};
`;

const Content = styled.div`
  margin-left: 40px;
`;

const NotifTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 1.1rem;
  color: ${props => props.$read ? theme.colors.textSecondary : 'white'};
`;

const Message = styled.p`
  margin: 0;
  color: ${theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.5;
`;

const TimeStamp = styled.div`
  margin-top: 8px;
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  text-align: right;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: bold;
  margin-left: 8px;
  background: ${props => props.$isSystem ? 'rgba(255, 152, 0, 0.3)' : 'rgba(33, 150, 243, 0.3)'};
  color: ${props => props.$isSystem ? '#ff9800' : '#2196f3'};
  border: 1px solid ${props => props.$isSystem ? '#ff9800' : '#2196f3'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${theme.colors.textMuted};
  
  svg {
    font-size: 4rem;
    margin-bottom: 20px;
    opacity: 0.3;
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left-color: ${theme.colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 40px auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const getBorderColor = (type) => {
  switch (type) {
    case 'success': return theme.colors.success;
    case 'error': return theme.colors.error;
    case 'warning': return '#ff9800'; // Orange
    case 'gift_card': return '#D4AF37'; // Gold
    case 'points': return theme.colors.primary;
    case 'news': return '#2196f3'; // Blue
    default: return theme.colors.textMuted;
  }
};

const getIcon = (type) => {
  switch (type) {
    case 'success': return <FaCheckCircle />;
    case 'error': return <FaExclamationTriangle />;
    case 'gift_card': return <FaGift />;
    case 'points': return <FaCoins />;
    case 'news': return <FaNewspaper />;
    default: return <FaInfoCircle />;
  }
};

const getIconColor = (type) => {
    switch (type) {
      case 'success': return theme.colors.success;
      case 'error': return theme.colors.error;
      case 'warning': return '#ff9800'; 
      case 'gift_card': return '#D4AF37'; 
      case 'points': return theme.colors.primary;
      case 'news': return '#2196f3'; 
      default: return theme.colors.textSecondary;
    }
  };

const Notifications = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    markAllAsReadOnOpen();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await ApiService.getNotifications(1, 50); // Fetch first 50
      if (res && res.data) {
        // Normalize Strapi response (handle both flattened and nested structures)
        const flattened = res.data.map(item => {
            if (item.attributes) {
                return { id: item.id, ...item.attributes };
            }
            return item;
        });
        setNotifications(flattened);
      }
    } catch (error) {
      // If unauthenticated, just show empty state
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsReadOnOpen = async () => {
      // Optional: Ask user or do it automatically? 
      // Usually "Mark all read" is a manual action, but visiting the page implies reading.
      // Keeping it manual for now via button, but we can auto-mark viewed ones.
  };

  const handleMarkAllRead = async () => {
    try {
      await ApiService.markAllNotificationsRead();
      // Backend now marks personal + global as read per-user
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (error) {
        // Silent fail
    }
  };

  const handleClick = async (notification) => {
    // Mark as read (personal rows update isRead; global rows create per-user receipt)
    if (!notification.isRead) {
      try {
        await ApiService.markNotificationRead(notification.id);
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
      } catch (e) { console.error(e); }
    }

    // Navigation action
    if (notification.actionUrl && onNavigate) {
      onNavigate(notification.actionUrl);
    }
  };

  if (loading) return (
    <Container>
      <Header>
          <Title><FaBell /> Notifications</Title>
      </Header>
      <LoadingSpinner />
    </Container>
  );

  return (
    <Container>
      <Header>
        <Title><FaBell /> Notifications</Title>
        {notifications.some(n => !n.isRead) && (
            <ActionButton onClick={handleMarkAllRead}>
            <FaCheckDouble /> Mark all read
            </ActionButton>
        )}
      </Header>

      {notifications.length === 0 ? (
        <EmptyState>
          <FaBell />
          <h3>No notifications yet</h3>
          <p>We'll notify you when important things happen.</p>
        </EmptyState>
      ) : (
        <NotificationList>
          {notifications.map(notification => (
            <NotificationCard 
              key={notification.id} 
              $read={notification.isRead}
              type={notification.type}
              $isGlobal={notification.isGlobal}
              $isSystem={notification.isSystem}
              $hasAction={!!notification.actionUrl}
              onClick={() => handleClick(notification)}
            >
              <IconWrapper color={getIconColor(notification.type)}>
                {getIcon(notification.type)}
              </IconWrapper>
              <Content>
                <NotifTitle $read={notification.isRead}>
                  {notification.title}
                  {notification.isSystem && <Badge $isSystem>SYSTEM</Badge>}
                  {notification.isGlobal && !notification.isSystem && <Badge>ANNOUNCEMENT</Badge>}
                </NotifTitle>
                <Message>{notification.message}</Message>
                <TimeStamp>{new Date(notification.createdAt).toLocaleString()}</TimeStamp>
              </Content>
            </NotificationCard>
          ))}
        </NotificationList>
      )}
    </Container>
  );
};

export default Notifications;
